import { IconChevronLeft } from '@tabler/icons-react';
import NSIcon from 'bricks/NSIcon/NSIcon';
import { NSBadge, NSButton, NSCard } from 'bricks';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { Nav, NavItem } from 'reactstrap';
import { URLS } from 'services/urls';
import { formatExcelCellValue } from 'ns_libs/formatter';
import { formatProFormaAssetTypeById } from 'helpers/proForma';
import { PRO_FORMA_TABLE_NAV } from 'views/ProFormaTable/constants';
import { IKeyStatsCard } from 'views/ProFormaTable/types';
import {
    ProFormaFooterKeyStatCard,
} from 'views/ProFormaTable/components/ProFormaSideNav/ProFormaSideNavFooter/ProFormaFooterKeyStatCard/ProFormaFooterKeyStatCard';
import { IExtractedField, IExtractedProForma } from 'types/db/ExtractedProForma';
import { EXECUTIVE_SUMMARY_NAV_ITEM, EXTRACTED_FROM_EXCEL_BADGE, KEY_STAT_FIELDS, NOT_LOCATED_VALUE } from '../constants';
import SourceWorkbook from './SourceWorkbook/SourceWorkbook';

export interface ExtractedProFormaSideNavProps {
    dealName?: string;
    extractedProForma: IExtractedProForma;
}

const buildKeyStats = (fields: IExtractedField[]): IKeyStatsCard[] => KEY_STAT_FIELDS.map(({ key, title, icon }) => {
    const field = fields.find(candidate => candidate.key === key);
    const isLocated = !!field && !field.cannotLocate;
    const displayValue = isLocated ? formatExcelCellValue(field.value, field.numberFormat) : NOT_LOCATED_VALUE;

    return {
        icon,
        title,
        value: isLocated ? Number(field.value) : null,
        comparisonValue: 0,
        formatter: () => displayValue,
    };
});

const ExtractedProFormaSideNav = ({ dealName, extractedProForma }: ExtractedProFormaSideNavProps) => {
    const navigate = useNavigate();
    const { dealId } = useParams<{ dealId: string }>();

    const fields = extractedProForma.extractedData?.fields ?? [];
    const keyStats = buildKeyStats(fields);
    const [firstStatRow, secondStatRow] = [keyStats.slice(0, 2), keyStats.slice(2, 4)];

    const navigateToProFormaList = () => navigate(generatePath(URLS.PRO_FORMA.HOME, { dealId }));

    return (
        <NSCard className="NSCard--level-2 ProFormaSideNav">
            <div className="px-3 pt-3">
                <NSButton
                    callback={navigateToProFormaList}
                    color="primary"
                    className="font-13 px-2 mb-2"
                    size="sm"
                    outline
                    allowReadOnlyAccess
                >
                    <NSIcon icon={IconChevronLeft} className="mr-1" size="xs" />
                    Pro Forma List
                </NSButton>
                <h4 className="text-dark mt-3 mb-0 font-weight-bold">{dealName}</h4>
                <div className="d-flex align-items-center mt-1">
                    <div className="font-12 text-muted text-uppercase">
                        {formatProFormaAssetTypeById(extractedProForma.proFormaModelId)}
                    </div>
                    <NSBadge color="primary-lighten" className="ml-2">{EXTRACTED_FROM_EXCEL_BADGE}</NSBadge>
                </div>
            </div>
            <SourceWorkbook extractedProForma={extractedProForma} />
            <hr className="mb-0 mt-3" />
            <div className="ProFormaSideNav__content">
                <Nav tabs className="ProFormaSideNav__nav flex-column w-100 border-0">
                    <NavItem className="bg-primary-lighten text-primary font-weight-bold d-flex align-items-center px-4 py-2">
                        <NSIcon icon={PRO_FORMA_TABLE_NAV.EXECUTIVE_SUMMARY.icon} className="py-1" />
                        <div className="pl-1">
                            <span>{EXECUTIVE_SUMMARY_NAV_ITEM}</span>
                        </div>
                    </NavItem>
                </Nav>
            </div>
            <NSCard className="NSCard--level-4 p-2 pt-1 mt-2 ProFormaSideNav__footer w-100">
                <div className="d-flex">
                    {firstStatRow.map((stat, index) => (
                        <ProFormaFooterKeyStatCard
                            key={stat.title}
                            index={index}
                            stat={stat}
                            keyStatsActualsDiscrepancies={{}}
                            shouldAnimate={false}
                        />
                    ))}
                </div>
                <div className="d-flex">
                    {secondStatRow.map((stat, index) => (
                        <ProFormaFooterKeyStatCard
                            key={stat.title}
                            index={index}
                            stat={stat}
                            keyStatsActualsDiscrepancies={{}}
                            shouldAnimate={false}
                        />
                    ))}
                </div>
            </NSCard>
        </NSCard>
    );
};

export default ExtractedProFormaSideNav;
